import { ReactNode } from "react";
import {
  Circle,
  FontSizeTokens,
  Text,
  Token,
  XStack,
  XStackProps,
} from "tamagui";

interface StatusPillThemeProps {
  outlined?: boolean;
  error?: boolean;
  info?: boolean;
  success?: boolean;
  waiting?: boolean;
  warning?: boolean;
}

export interface StatusPillProps extends XStackProps, StatusPillThemeProps {
  fontSize?: FontSizeTokens;
  hideDot?: boolean;
  children: ReactNode;
}

export const StatusPill = ({
  outlined,
  error,
  info,
  success,
  waiting,
  warning,
  fontSize = "$4",
  hideDot,
  children,
  ...rest
}: StatusPillProps) => {
  const { backgroundColor, dotColor, textColor } = getThemeTokens({
    outlined,
    error,
    info,
    success,
    waiting,
    warning,
  });

  return (
    <XStack
      width="fit-content"
      maxWidth="100%"
      gap="$2"
      alignItems="center"
      backgroundColor={backgroundColor}
      paddingVertical="$1"
      paddingHorizontal="$2"
      borderWidth={2}
      borderColor="$white5"
      borderRadius="$3"
      {...rest}
    >
      {!hideDot && <Circle size={8} backgroundColor={dotColor} />}
      <Text
        fontSize={fontSize}
        color={textColor}
        overflow="hidden"
        whiteSpace="nowrap"
        textOverflow="ellipsis"
      >
        {children}
      </Text>
    </XStack>
  );
};

// Colours are the tamagui-config status authority. On a filled pill the dot
// is the text colour; the Tamagui `$red3`-style tints it used to be were the
// second palette this design system is retiring.
const getThemeTokens = ({
  outlined,
  error,
  info,
  success,
  waiting,
  warning,
}: StatusPillThemeProps): {
  backgroundColor: Token;
  dotColor: Token;
  textColor: Token;
} => {
  if (error) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$statusCritical" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$statusCritical" as Token,
          dotColor: "$black12" as Token,
          textColor: "$black12" as Token,
        };
  }

  if (info) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$hgPrimary" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$hgPrimary" as Token,
          dotColor: "$black12" as Token,
          textColor: "$black12" as Token,
        };
  }

  if (success) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$statusGood" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$statusGood" as Token,
          dotColor: "$black12" as Token,
          textColor: "$black12" as Token,
        };
  }

  if (waiting) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$statusOffline" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$statusOffline" as Token,
          dotColor: "$black12" as Token,
          textColor: "$black12" as Token,
        };
  }

  if (warning) {
    return outlined
      ? {
          backgroundColor: "$transparentBorder" as Token,
          dotColor: "$statusWarning" as Token,
          textColor: "$white12" as Token,
        }
      : {
          backgroundColor: "$statusWarning" as Token,
          dotColor: "$black12" as Token,
          textColor: "$black12" as Token,
        };
  }

  return outlined
    ? {
        backgroundColor: "$transparentBorder" as Token,
        dotColor: "$gray9" as Token,
        textColor: "$white12" as Token,
      }
    : {
        backgroundColor: "$gray12" as Token,
        dotColor: "$gray3" as Token,
        textColor: "$black12" as Token,
      };
};
